"use client";

import Link from "next/link";
import { useSyncExternalStore } from "react";

/* Il carrello.
 *
 * Non c'è un account, e questa pagina non finge che ci sia: legge quello che
 * **questo browser** si ricorda. È onesto anche perché è così che funziona il
 * resto — una configurazione e un ordine si raggiungono con un token, non con
 * un accesso, e il token vive nel browser di chi l'ha creato.
 *
 * Il limite va detto invece che nascosto: da un altro dispositivo, o dopo aver
 * pulito i dati del sito, questa pagina è vuota. I link degli ordini però
 * continuano a funzionare ovunque, ed è per questo che conviene conservarli. */

type Voce = { chiave: string; token: string };
type Contenuto = { configurazioni: Voce[]; ordini: Voce[] };

function leggi(prefisso: string): Voce[] {
  try {
    const trovate: Voce[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const chiave = window.localStorage.key(i);
      if (!chiave?.startsWith(prefisso)) continue;
      const token = window.localStorage.getItem(chiave);
      if (token) trovate.push({ chiave, token });
    }
    return trovate;
  } catch {
    // Modalità privata o archiviazione negata: non c'è niente da ricordare.
    return [];
  }
}

/* `localStorage` è uno stato che vive fuori da React: leggerlo con uno stato più
   un effetto significherebbe un render in più a ogni visita. Si legge con lo
   strumento fatto per questo, con una copia in cache — `useSyncExternalStore`
   pretende che due letture di fila diano lo **stesso oggetto**, altrimenti
   rirenderizza all'infinito. La cache si butta quando cambia qualcosa. */
const VUOTO: Contenuto = { configurazioni: [], ordini: [] };
let cache: Contenuto | null = null;
const ascoltatori = new Set<() => void>();

function contenuto(): Contenuto {
  if (!cache) {
    cache = {
      configurazioni: leggi("youppybuy:configurazione:"),
      ordini: leggi("youppybuy:ordine:"),
    };
  }
  return cache;
}

function sottoscrivi(avvisa: () => void) {
  const suCambio = () => {
    cache = null;
    ascoltatori.forEach((a) => a());
  };
  ascoltatori.add(avvisa);
  window.addEventListener("storage", suCambio);
  return () => {
    ascoltatori.delete(avvisa);
    window.removeEventListener("storage", suCambio);
  };
}

export default function CarrelloPage() {
  // Sul server non c'è archiviazione: si parte da vuoto e si riempie appena il
  // browser prende in mano la pagina.
  const { configurazioni, ordini } = useSyncExternalStore(
    sottoscrivi,
    contenuto,
    () => VUOTO,
  );

  const vuoto = configurazioni.length === 0 && ordini.length === 0;

  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">Il tuo carrello</h1>
      <p className="mt-2 text-muted">
        Le commesse che hai cominciato a configurare e gli ordini che hai fatto da
        questo browser.
      </p>

      {vuoto ? (
        <div className="mt-8 rounded-xl border border-dashed border-line p-8 text-center">
          <p className="text-muted">
            Non c&apos;è ancora niente. Le configurazioni compaiono qui appena ne
            cominci una da una scheda prodotto.
          </p>
          <Link
            href="/prodotti"
            className="mt-4 inline-block rounded-lg bg-accent px-4 py-2 font-medium text-accent-contrast"
          >
            Sfoglia il catalogo
          </Link>
        </div>
      ) : (
        <>
          {configurazioni.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-semibold">Configurazioni in corso</h2>
              <ul className="mt-3 divide-y divide-line rounded-xl border border-line">
                {configurazioni.map(({ chiave, token }) => (
                  <li key={chiave} className="flex flex-wrap items-center gap-3 p-4">
                    <span className="flex-1 text-sm">
                      Commessa su{" "}
                      <Link
                        href={`/products/${chiave.split(":").pop()}`}
                        className="text-accent underline underline-offset-2"
                      >
                        questo prodotto
                      </Link>
                    </span>
                    <Link
                      href={`/checkout/${token}`}
                      className="rounded-lg border border-line px-3 py-1.5 text-sm transition hover:border-accent"
                    >
                      Riprendi
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted">
                Le bozze lasciate a metà si cancellano dopo novanta giorni, insieme
                agli allegati.
              </p>
            </section>
          )}

          {ordini.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-semibold">I tuoi ordini</h2>
              <ul className="mt-3 divide-y divide-line rounded-xl border border-line">
                {ordini.map(({ chiave, token }) => (
                  <li key={chiave} className="flex flex-wrap items-center gap-3 p-4">
                    <span className="flex-1 text-sm font-medium">
                      {chiave.split(":").pop()}
                    </span>
                    <Link
                      href={`/orders/${token}`}
                      className="rounded-lg border border-line px-3 py-1.5 text-sm transition hover:border-accent"
                    >
                      Apri
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <p className="mt-10 rounded-lg border border-line bg-surface p-4 text-xs text-muted">
        Questa pagina legge quello che <strong>questo browser</strong> si ricorda: non
        c&apos;è ancora un account, quindi da un altro dispositivo la trovi vuota. I
        link degli ordini però continuano a funzionare da qualunque parte — vale la
        pena conservarli.
      </p>
    </div>
  );
}
